import React, { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Typography } from '@mui/material';

import SimpleTextInput from '../Components/SimpleTextInput';
import KebabInput from '../Components/KebabInput';
import MRNInput from '../Components/MRNInput';
import RightLink from '../Components/RightLink';
import NeenorAutocomplete from '../Components/NeenorAutocomplete';
import { ConsignmentDetailsModal, InvalidMRNModal } from '../Modals/ConsignmentModals';
import { GBENSContext } from '../GBENSContainer';
import ReadOnlyInput from '../Components/ReadOnlyInput';

import { useStepper } from '../../../components/Stepper';
import { getMRNDetails } from '../../../api/GBENSDec';
import PDFUploader from '../Components/PDFUploader';
import TextAndSelectInput from '../Components/TextAndSelectInput';
import { uploadPDF, downloadPDF } from '../../../api/_helper';
import { Buffer } from 'buffer';

const AddingConsignments = ({ handleGetMRNDetails, isUpdating }) => {
  const [consignmentDetailsModalOpen, setConsignmentDetailsModalOpen] = useState(false);
  const [invalidMRNModalOpen, setInvalidMRNModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: '',
    body: '',
  });
  const [pdfFileName, setPdfFileName] = useState('');

  const screen = 'Consignments';

  const {
    currentConsignment,
    data,
    handleShowWarningModal,
    updateCurrentConsignment,
    addNewConsignmentLine,
    editConsignmentLine,
    referenceData,
    deleteConsignment,
    isReadOnly,
  } = useContext(GBENSContext);

  const { gotoStep } = useStepper();

  let isConsignmentEditable = true;
  if (isReadOnly) {
    isConsignmentEditable = false;
  } else if (currentConsignment?.id) {
    isConsignmentEditable = currentConsignment?.isEditable;
  }

  const clickEditLine = (id) => {
    if (!currentConsignment || !Array.isArray(currentConsignment.lines) || currentConsignment.lines.length === 0) {
      clickNewLine();
    } else {
      editConsignmentLine(id);
      setConsignmentDetailsModalOpen(true);
    }
  };

  const clickNewLine = () => {
    addNewConsignmentLine();
    setConsignmentDetailsModalOpen(true);
  };

  const [isExtractingData, setIsExtractingData] = useState(false);
  const [extractedData, setExtractedData] = useState('');

  // enable document upload if this is a new consignment, or the consignment is a draft
  const enableUpload = !isUpdating || currentConsignment.statusId === 10 || !currentConsignment.statusId;

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setPdfFileName(file.name);

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      try {
        //if the file.name does not end with .pdf, throw an error with the error message 'Please upload a valid PDF file'
        if (!file.name.endsWith('.pdf')) {
          throw new Error(JSON.stringify({ error: 'Please upload a valid PDF file' }));
        }
        setIsExtractingData(true);
        const response = await uploadPDF(formData);
        //if response[0]?.message is not 'Uploaded successfully', then throw an error with the error message 'There was an error uploading the PDF'
        if (response[0]?.message !== 'Uploaded successfully') {
          throw new Error(JSON.stringify({ error: 'There was an error uploading the PDF' }));
        }

        if (response[0]?.blobPathName) {
          updateCurrentConsignment({ target: { value: response[0]?.blobPathName } }, 'blobPathName');
        }

        handleGetMRNDetails(response);
        setExtractedData(response[0]);
        setIsExtractingData(false);
      } catch (error) {
        if (error?.message) {
          setPdfFileName('');
          setIsExtractingData(false);
          // Parse the error message to extract user-friendly text
          let errorText = error.message;
          try {
            const parsedError = JSON.parse(error.message);
            if (parsedError.error) {
              errorText = parsedError.error;
            }
          } catch (parseError) {
            // If parsing fails, use the original message
            errorText = error.message;
          }

          setModalMessage({
            title: 'Error with Export Document',
            body: errorText,
          });
          setInvalidMRNModalOpen(true);
        }
      }
    }
  };

  const onExportDocumentClick = async () => {
    if (currentConsignment?.id) {
      const buffer = await downloadPDF(currentConsignment?.id);
      if (buffer?.buffer?.data) {
        const dataBuffered = Buffer.from(buffer.buffer.data, 'binary').toString('base64');

        const pdfData = atob(dataBuffered);
        const pdfArray = new Uint8Array(pdfData.length);
        for (let i = 0; i < pdfData.length; i++) {
          pdfArray[i] = pdfData.charCodeAt(i);
        }
        const blob = new Blob([pdfArray], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(blob);

        window.open(pdfUrl);
      }
    }
  };

  const onNext = async () => {
    if (!isConsignmentEditable) {
      gotoStep('Consignments');
    } else {
      handleShowWarningModal(isUpdating ? 'UpdatingConsignment' : 'AddingConsignment', () => gotoStep('Consignments'));
    }
  };

  const handleMRNClick = async (inputValue) => {
    try {
      if (!inputValue) {
        updateCurrentConsignment({ target: { value: '' } }, 'cabieImportMrn');
        return;
      }
      const data = await getMRNDetails(inputValue);
      updateCurrentConsignment({ target: { value: inputValue } }, 'cabieImportMrn');
      handleGetMRNDetails(data);
    } catch (err) {
      console.log(err);
      setModalMessage({
        title: 'Errors detected with the MRN',
        body: 'The MRN you have entered could not be linked to a CABIE Declaration',
      });
      setInvalidMRNModalOpen(true);
    }
  };

  const clickDeleteConsignment = () => {
    deleteConsignment(currentConsignment.id);
    gotoStep('Consignments');
  };

  const blobPathName = currentConsignment?.blobPathName ?? '';
  const exportDocumentName = currentConsignment.consignmentLRN
    ? blobPathName
    : blobPathName?.split('_')?.slice(2)?.join('_');

  return (
    <>
      <ConsignmentDetailsModal
        open={consignmentDetailsModalOpen}
        setOpen={setConsignmentDetailsModalOpen}
        disabled={!isConsignmentEditable}
      />
      <InvalidMRNModal open={invalidMRNModalOpen} setOpen={setInvalidMRNModalOpen} modalMessage={modalMessage} />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        {enableUpload && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 5 }}>
              <Typography
                id="modal-modal-title"
                variant="h5"
                component="h2"
                style={{ marginLeft: 30, fontWeight: 'bold' }}
              >
                EORI Customer?
              </Typography>
              <Typography
                id="modal-modal-title"
                variant="p"
                component="p"
                style={{ marginLeft: 10, alignItems: 'baseline', marginTop: 8 }}
              >
                If you have an MRN, enter it now to link add the consignment.
              </Typography>
            </div>

            <MRNInput
              name="CABIE Export MRN"
              consignment
              id="MRN"
              onClick={(inputValue) => handleMRNClick(inputValue)}
              disabled={!isConsignmentEditable}
            />

            <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 5 }}>
              <Typography
                id="modal-modal-title"
                variant="h5"
                component="h2"
                style={{ marginLeft: 30, fontWeight: 'bold' }}
              >
                Extract from Export Document
              </Typography>
            </div>

            <PDFUploader
              name="Upload Export Document"
              id="blobPathName"
              handleFileChange={handleFileChange}
              pdfFileName={pdfFileName}
              isLoading={isExtractingData}
              extractedData={extractedData}
              disabled={!isConsignmentEditable}
            />
          </>
        )}
        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Header
          </Typography>
        </div>
        <SimpleTextInput
          name="Consignment Reference*"
          consignment
          placeholder="Your reference / Dispatch note / Invoice number / etc"
          id="consignmentReference"
          screen={screen}
          disabled={!isConsignmentEditable}
        />
        <TextAndSelectInput
          name="Transport Document"
          screen={screen}
          id="transportDocument"
          options={referenceData.getDocumentType}
          selectPlaceholder="Doc Type"
          textPlaceholder="Document Reference"
          selectId="transportDocTypeId"
          textId="transportDocReference"
          consignment
          toolTip="Optional field - the system will generate a unique reference based on the master document if no consignment document is supplied"
          disabled={!isConsignmentEditable}
        />
        <NeenorAutocomplete
          name="Consignor Details*"
          modal="Consignor"
          placeholder="Type to find, or add new consignor details"
          id="Consignor"
          options={referenceData.consignors}
          consignment
          disabled={!isConsignmentEditable}
        />
        <NeenorAutocomplete
          name="Seller Details"
          modal="Seller"
          placeholder="Enter if the Seller details are different to the Consignor"
          id="Seller"
          options={referenceData.consignors}
          consignment
          toolTip="Defaults to Consignor details unless if left blank"
          disabled={!isConsignmentEditable}
        />
        <NeenorAutocomplete
          name="Consignee Details*"
          modal="Consignee"
          placeholder="Type to find, or add new consignee details"
          id="Consignee"
          options={referenceData.consignees}
          consignment
          disabled={!isConsignmentEditable}
        />
        <NeenorAutocomplete
          name="Buyer Details"
          modal="Buyer"
          placeholder="Enter if the Buyer details are different to the Consignee"
          id="Buyer"
          options={referenceData.consignees}
          consignment
          toolTip="Defaults to Consignee details unless if left blank"
          disabled={!isConsignmentEditable}
        />
        <TextAndSelectInput
          name="Place of Acceptance*"
          screen={screen}
          id="placeOfAcceptance"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="Country"
          textPlaceholder="Place Name"
          selectId="placeOfAcceptanceCountry"
          textId="placeOfAcceptancePlaceName"
          consignment
          toolTip="Defaults to the Transport and Journey 'Place of Acceptance' unless specific location entered"
          disabled={!isConsignmentEditable}
        />
        <TextAndSelectInput
          name="Place of Delivery*"
          screen={screen}
          id="placeOfDelivery"
          options={referenceData.countryOfOriginCC}
          selectPlaceholder="Country"
          textPlaceholder="Place Name"
          selectId="placeOfDeliveryCountry"
          textId="placeOfDeliveryPlaceName"
          consignment
          toolTip="Defaults to Consignee address unless specific delivery location entered"
          disabled={!isConsignmentEditable}
        />
        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 5 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Details
          </Typography>
        </div>
        {(currentConsignment?.lines?.length ? currentConsignment.lines : [{ id: 0, goodsDescription: '' }])?.map?.(
          (c, i) => {
            return (
              <KebabInput
                key={i}
                name={`Line ${i + 1}:`}
                value={c.goodsDescription}
                placeholder="Click link to add"
                onClick={() => clickEditLine(c.id)}
                consignmentLine
                screen={screen}
                id={c.id}
                disabled={!isConsignmentEditable}
              />
            );
          }
        )}
        {!!isConsignmentEditable && <RightLink name="Add Additional Line" onClick={clickNewLine} />}
        {isUpdating && (
          <>
            <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
              <Typography
                id="modal-modal-title"
                variant="h5"
                component="h2"
                style={{ marginLeft: 30, fontWeight: 'bold' }}
              >
                Additional Information
              </Typography>
            </div>
            <ReadOnlyInput
              toolTip="If a CABIE Export MRN has been used to create the consignment, the reference will appear here."
              value={currentConsignment?.cabie_mrn || currentConsignment?.cabieImportMrn || ''}
              name="CABIE Export MRN"
            />
            <ReadOnlyInput
              toolTip="If a document has been used to create the consignment, the reference will appear here."
              value={exportDocumentName}
              name="Export Document"
              onClick={onExportDocumentClick}
            />
            <ReadOnlyInput
              toolTip="The LRN is a unique number for the shipment."
              value={currentConsignment?.shipmentLRN ?? ''}
              name="ICS2 Shipment LRN"
            />
            <ReadOnlyInput
              toolTip="The LRN is a unique number for the consignment."
              value={currentConsignment?.consignmentLRN ?? ''}
              name="ICS2 Consignment LRN"
            />
            <ReadOnlyInput
              toolTip="The date and time the consignment was sent to HMRC."
              value={currentConsignment?.Created ?? ''}
              name="Created"
            />
            {currentConsignment?.previousLRN && (
              <ReadOnlyInput
                //toolTip="The date and time the consignment was sent to HMRC."
                value={currentConsignment?.previousLRN}
                name="Amendment"
              />
            )}
          </>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          paddingTop: 20,
        }}
      >
        {isUpdating && currentConsignment.statusId !== 20 && currentConsignment.statusId !== 30 ? (
          <Button variant="outlined" onClick={clickDeleteConsignment} disabled={!isConsignmentEditable}>
            Delete this Consignment
          </Button>
        ) : (
          <span></span>
        )}
        <Button variant="outlined" onClick={onNext} disabled={!isConsignmentEditable}>
          Save Consignment
        </Button>
      </div>
    </>
  );
};

AddingConsignments.propTypes = {
  handleGetMRNDetails: PropTypes.func.isRequired,
};

export default AddingConsignments;
